import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";
import { useToast } from "../components/ui/Toast";
import { Lightbulb, Users, Package, Eye, Trash2, Loader2, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import { useAdminApi } from '../apis/admin-apis';
import { useSkillsApi } from '../apis/skills-apis';
import { useAgentsApi } from '../apis/agents-apis';
import { usePackagesApi } from '../apis/packages-apis';
import { usePromptsApi } from '../apis/prompts-apis';
import { AdminData } from '../types';
import { ConfirmationDialog } from '../components/ConfirmationDialog';

type ItemType = 'skill' | 'agent' | 'package' | 'prompt';

const formatItemType = (type: ItemType | undefined): string => {
  if (!type) return 'Item';
  return type.charAt(0).toUpperCase() + type.slice(1);
};

const CoreItemsPage: React.FC = () => {
  const adminApi = useAdminApi();
  const skillsApi = useSkillsApi();
  const agentsApi = useAgentsApi();
  const packagesApi = usePackagesApi();
  const promptsApi = usePromptsApi();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminData, setAdminData] = useState<AdminData | null>(null);
  const { addToast } = useToast();
  const [expandedSection, setExpandedSection] = useState<ItemType | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{id: string; type: ItemType} | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const response = await adminApi.getAdminData();
        setAdminData(response);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        addToast({
          title: "Error",
          description: "Failed to load administrative data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [addToast]);

  const handleView = (type: ItemType, id: string) => {
    navigate(`/marketplace/${type}s/edit/${id}`);
  };

  const confirmDelete = (type: ItemType, id: string) => {
    setItemToDelete({ type, id });
    setDeleteDialogOpen(true);
  };

  const getDeleteFunction = (type: ItemType) => {
    switch (type) {
      case 'skill':
        return skillsApi.deleteSkill;
      case 'agent':
        return agentsApi.deleteAgent;
      case 'package':
        return packagesApi.deletePackage;
      case 'prompt':
        return promptsApi.deletePrompt;
      default:
        throw new Error(`Unknown type: ${type}`);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!itemToDelete) return;

    setIsDeletingId(itemToDelete.id);
    try {
      const deleteFunction = getDeleteFunction(itemToDelete.type);
      await deleteFunction(itemToDelete.id);
      
      addToast({
        title: "Success",
        description: `${formatItemType(itemToDelete.type)} successfully deleted`,
        variant: "default",
      });

      // Refresh admin data
      const response = await adminApi.getAdminData();
      setAdminData(response);
    } catch (error) {
      console.error(`Error deleting ${itemToDelete.type}:`, error);
      addToast({
        title: "Error",
        description: `Failed to delete ${itemToDelete.type}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const ItemCard: React.FC<{
    title: string;
    description: string;
    id: string;
    type: ItemType;
    tags?: string[];
  }> = ({ title, description, id, type, tags }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-grow">
            <h2 className="text-xl font-semibold">{title}</h2>
            <p className="text-muted-foreground mt-2">{description}</p>
            {tags && tags.length > 0 && (
              <div className="relative">
                <div className="flex flex-wrap gap-1 mt-2">
                  {tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium ring-1 ring-inset ring-muted-foreground/20"
                    >
                      {tag}
                    </span>
                  ))}
                  {tags.length > 3 && (
                    <div className="relative inline-block group">
                      <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium ring-1 ring-inset ring-muted-foreground/20 cursor-pointer">
                        +{tags.length - 3}
                      </span>
                      <div className="absolute left-0 mt-2 p-2 bg-white border border-gray-200 rounded-md shadow-lg z-10 hidden group-hover:block">
                        {tags.slice(3).map((tag, index) => (
                          <span
                            key={index}
                            className="block text-xs text-gray-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 ml-4">
            <Button
              variant="secondary"
              onClick={() => handleView(type, id)}
              className="bg-blue-100 hover:bg-blue-200 text-blue-600"
              disabled={isDeletingId === id}
            >
              <Eye className="mr-2 h-4 w-4" />
              View
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmDelete(type, id)}
              className="bg-red-500 hover:bg-red-600 text-white"
              disabled={isDeletingId === id}
            >
              {isDeletingId === id ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const Section: React.FC<{
    title: string;
    icon: React.ReactNode;
    items: any[];
    type: ItemType;
  }> = ({ title, icon, items, type }) => {
    const isExpanded = expandedSection === type;
    const recentItems = [...items]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);

    return (
      <Card 
        className="mb-8 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setExpandedSection(isExpanded ? null : type)}
      >
        <CardContent className="p-0">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {icon}
                <h2 className="text-2xl font-semibold">{title}</h2>
              </div>
              <div className="flex items-center gap-4">
                {isExpanded ? (
                  <ChevronUp className="w-6 h-6 text-gray-500" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-gray-500" />
                )}
              </div>
            </div>
          </div>

          {isExpanded && (
            <div className="border-t border-gray-200">
              <div className="p-6 space-y-4">
                {recentItems.map((item) => (
                  <ItemCard
                    key={item[`${type}Id`]}
                    id={item[`${type}Id`]}
                    type={type}
                    title={item[`${type}Name`]}
                    description={item[`${type}Description`]}
                    tags={item[`${type}Tags`]}
                  />
                ))}
                {recentItems.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No items found</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="w-8 h-8" />
          <p className="text-gray-600">Loading administrative data...</p>
        </div>
      </div>
    );
  }

  if (!adminData) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <p className="text-xl text-gray-600">No administrative data available</p>
      </div>
    );
  }

  const formattedItemType = formatItemType(itemToDelete?.type);

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-8"><center>Core Items</center></h1>

      <Section
        title="Skills"
        icon={<Lightbulb className="w-8 h-8 text-yellow-500" />}
        items={adminData.skills}
        type="skill"
      />

      <Section
        title="Agents"
        icon={<Users className="w-8 h-8 text-blue-500" />}
        items={adminData.agents}
        type="agent"
      />

      <Section
        title="Packages"
        icon={<Package className="w-8 h-8 text-green-500" />}
        items={adminData.packages}
        type="package"
      />

      <Section
        title="Prompts"
        icon={<MessageSquare className="w-8 h-8 text-purple-500" />}
        items={adminData.prompts}
        type="prompt"
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title={`Delete ${formattedItemType}`}
        description={`This will permanently delete the ${itemToDelete?.type || 'item'} from the system. This action cannot be undone.`}
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={true}
        verificationWord="DELETE"
      />
    </div>
  );
};

export default CoreItemsPage;